import { CanActivate, ExecutionContext, Injectable, UnauthorizedException } from '@nestjs/common';
import { Request } from 'express';
import { AuthService } from './auth.service';

export interface AuthUser {
  id: string;
  email: string;
  role: string;
}

/**
 * A request that MAY carry a user — the shape before any guard has run, and
 * what the guard itself receives.
 */
export interface AuthenticatedRequest extends Request {
  user?: AuthUser;
}

/**
 * A request on a route that is behind `AuthGuard`, where `user` is guaranteed.
 *
 * Controllers were reaching for `request.user!.id` on every guarded handler —
 * 99 non-null assertions across the API, each one a place where the compiler
 * had been told to stop checking. The guard genuinely does set `user` before
 * any handler runs, so the honest fix is to say so in the type once rather
 * than assert it at every call site: the assertion silences the checker even
 * on routes where someone later forgets the guard, which is exactly the case
 * it should be catching.
 */
export interface GuardedRequest extends Request {
  user: AuthUser;
}

@Injectable()
export class AuthGuard implements CanActivate {
  constructor(private readonly authService: AuthService) {}

  canActivate(context: ExecutionContext): boolean {
    const request = context.switchToHttp().getRequest<AuthenticatedRequest>();
    const token = this.extractToken(request);
    const payload = this.authService.verifyToken(token);

    request.user = {
      id: payload.sub,
      email: payload.email,
      role: payload.role,
    };

    return true;
  }

  private extractToken(request: Request): string {
    const [scheme, token] = request.headers.authorization?.split(' ') ?? [];

    if (scheme !== 'Bearer' || !token) {
      throw new UnauthorizedException('Authorization bearer token is required');
    }

    return token;
  }
}
